import { useEffect, useRef, useState } from 'react';
import QRCode from 'react-qr-code';
import { Button, Heading, Image, Spinner, Stack, Text } from '@chakra-ui/react';
import { format, addSeconds, isPast } from 'date-fns';
import pt from 'date-fns/locale/pt';

import { useToast } from '../../../hooks/toast';

import { Buttons, Container, Content, ContentData } from './styles';
import api from '../../../services/api';
import Loading from '../../Loading';

type PixParams = {
	user_id: string,
	user_nome: string,
	user_doc: string,
	valor: string,
	chave_pix: string,
	id_baixa: string,
	documentos: string[],
}

interface Params {
	onClose: (close: boolean) => void;
	isVisible: boolean;
	pix_params: PixParams;
}

const ModalPagPix: React.FC<Params> = ({ onClose = () => { }, isVisible, pix_params }) => {
	const { addToast } = useToast();
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const [visible, setVisible] = useState(isVisible);

	const [isLoading, setIsLoading] = useState(true);
	const [isLoadingCopyPix, setIsLoadingCopyPix] = useState(false);

	const [pixData, setPixData] = useState<any>({} as any);

	const copyPix = async () => {
		try {
			setIsLoadingCopyPix(true);
			await navigator.clipboard.writeText(pixData.pixCopiaECola);

			addToast({
				type: 'success',
				title: 'Código PIX',
				description: 'O código PIX foi copiado!',
			});

		} catch (err) {
			addToast({
				type: 'error',
				title: 'Código PIX',
				description: 'Erro ao copiar o código PIX!',
			});
		} finally {
			setIsLoadingCopyPix(false);
		}
	};

	function handleOnClose() {
		setVisible(false);
		setTimeout(() => {
			onClose(false);
		}, 300);
	};

	async function handleCreatePix() {
		try {
			const clean = pix_params.user_doc.replace(/\D/g, '');
			const devedor = clean.length > 11 ? { cnpj: clean, nome: pix_params.user_nome } : { cpf: clean, nome: pix_params.user_nome };

			const body = {
				phone_devedor: null,
				params_pix: {
					calendario: {
						expiracao: 3600
					},
					devedor,
					valor: {
						original: pix_params.valor?.toString(),
						modalidadeAlteracao: 0
					},
					chave: "95127446000198" // pix_params.chave_pix
				}
			}

			console.log('body ==> ', body);
			console.log('pixParams ==> ', pix_params);

			const { data } = await api.post('/bb/pix', body);

			if (data.calendario) {
				const { criacao, expiracao } = data.calendario;
				const auxData = data;

				if (criacao && expiracao) {
					auxData.calendario.criacao = format(new Date(criacao), "dd'/'MM'/'yyyy 'ás' HH:mm", { locale: pt });
					const newExpiracao = addSeconds(new Date(criacao), expiracao);
					auxData.calendario.expired = isPast(new Date(newExpiracao));
					auxData.calendario.expiracao = format(new Date(newExpiracao), "dd'/'MM'/'yyyy 'ás' HH:mm", { locale: pt });
				}
			};

			setPixData({...data});
			console.log('Pix ==> ', data);

			await api.post('/portal/financeiro/pix', {
				id_baixa: pix_params.id_baixa,
				txid: data.txid,
				documentos: pix_params.documentos,
			});

			await verificar();

		} catch (error) {
			console.log('ERROR PIX ==> ', error);

		} finally {
			setIsLoading(false);
		}
	};

	const [status, setStatus] = useState('ATIVA');
	let ativo = true;

	async function verificar() {
		try {
			if (!pixData.txid) return;

			const { data } = await api.get(`/bb/pix/${pixData.txid}`);

			console.log('verificar ==> ', data);
			console.log('txid ==> ', pixData.txid);

			// if (!ativo) return;

			const novoStatus = data.status;
			setStatus(novoStatus);

			// 🔥 condição de parada automática
			if (novoStatus === 'PAGO' || novoStatus === 'CANCELADO') {
				if (timeoutRef.current)
					clearTimeout(timeoutRef.current);
				
				return;
			};

			timeoutRef.current = setTimeout(verificar, 5000);

		} catch (err) {
			console.error(err);

			if (timeoutRef.current)
				timeoutRef.current = setTimeout(verificar, 5000);
		}
	}

	useEffect(() => {
		const fetch = async () => await handleCreatePix();
		fetch();
	}, []);

	return (
		<Container id="ModalPagPix">
			{isLoading ? (
				<Loading />
			) : (
				<Content className={`${visible ? 'active' : ''}`}>
					<ContentData>
					{pixData.calendario ? (
						<Stack
							flex={1}
							width="100%"
							justify="center"
							align="center"
							borderWidth={4}
							borderColor={`${pixData.status === 'CONCLUIDA'
								? 'green.600'
								: pixData.status === 'ATIVA' && pixData.calendario.expired
									? 'red.500'
									: 'gray.300'
								}`}
							borderRadius={10}
							pt={10}
							pb={5}
							px={4}
						>
							<Heading fontSize={20} pb={5} pt={3}>Pagamento via Pix</Heading>

							<Stack width="100%">
								{(pixData.devedor && pixData.devedor.nome) && (
									<Stack flexDir="row">
										<Text fontWeight={600}>Cliente:</Text>
										<Text fontWeight={600}>{pixData.devedor.nome}</Text>
									</Stack>
								)}

								{pixData.devedor && (pixData.devedor.cpf || pixData.devedor.cnpj) && (
									<Stack flexDir="row">
										<Text fontWeight={600}>Doc.:</Text>
										<Text fontWeight={600}>{pixData.devedor.cpf ? pixData.devedor.cpf : pixData.devedor.cnpj}</Text>
									</Stack>
								)}

								{pixData.valor && pixData.valor.original && (
									<Stack flexDir="row">
										<Text fontWeight={600}>Valor:</Text>
										<Text fontWeight={600}>{pixData.valor.original}</Text>
									</Stack>
								)}

								{pixData.calendario && pixData.calendario.criacao && (
									<Stack flexDir="row">
										<Text fontWeight={600}>Data da geração:</Text>
										<Text fontWeight={500}>{pixData.calendario.criacao}</Text>
									</Stack>
								)}

								{pixData.calendario && pixData.calendario.expiracao && (
									<Stack flexDir="row">
										<Text fontWeight={600}>Expira em:</Text>
										<Text fontWeight={500}>{pixData.calendario.expiracao}</Text>
									</Stack>
								)}
							</Stack>

							{pixData.status === 'CONCLUIDA' && (
								<Heading py={4} color="green.600">Pix Pago!</Heading>
							)}

							{(pixData.calendario.expired && pixData.status === 'ATIVA') && (
								<Heading py={4} color="red.500">Pix Expirado!</Heading>
							)}

							{((pixData.pixCopiaECola && pixData.status === 'ATIVA' && !pixData.calendario.expired)
								|| (pixData.brcode && pixData.status === 'ATIVA' && !pixData.calendario.expired))
								&& (
									<>
										<QRCode
											size={256}
											style={{
												height: "auto", maxWidth: "30%", width: "30%", borderWidth: 4, borderColor: "black",
												padding: 8, borderRadius: 8, margin: 12
											}}
											value={pixData.pixCopiaECola || pixData.brcode}
											viewBox={`0 0 255 255`}
										/>

										<Text fontSize={14} fontWeight={500} wordBreak="break-word">{pixData.pixCopiaECola || pixData.brcode}</Text>

										<Button type="submit" disabled={isLoadingCopyPix} onClick={copyPix}>
											Copiar código PIX
											{isLoadingCopyPix && (
												<Spinner color="white" />
											)}
										</Button>
									</>
								)}

							<Stack flexDir="row" pt={6}>
								<Image
									src={'https://safetycloud.blob.core.windows.net/logo-cliente/raziodigital.png'}
									fit={{ base: "contain", md: "contain", lg: "cover" }}
									width={"200px"}
									alt="Logo Empresa"
								/>
							</Stack>
						</Stack>
					) : (
						<Stack
							flex={1}
							width="100%"
							justify="center"
							align="center"
							borderWidth={4}
							borderColor={`
								${pixData.status === 'CONCLUIDA'
									? 'green.600'
									: pixData.status === 'ATIVA' && pixData.calendario.expired
										? 'red.500'
										: 'gray.300'
								}`}
							borderRadius={10}
							pt={10}
							pb={5}
							mb={5}
							px={4}
						>
							<Heading>Pix não encontrado!</Heading>
						</Stack>
					)}

						</ContentData>

					<Buttons>
						<button
							type="button"
							className="button-cancel"
							onClick={() => {
								if (timeoutRef.current)
									clearTimeout(timeoutRef.current);
								
								handleOnClose();
							}}
						>
							Cancelar
						</button>
					</Buttons>
				</Content>
			)}
		</Container>
	)
}

export default ModalPagPix;